import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "material-ui/styles";
import Title from '../common/Title';
import { isEnableDisplayDepartmentTel } from '../../../../../utils/userUtils';
import { formatDepartmentTel } from '../../../../../utils/executiveListUtils';
import { PromotePlanDialogTypes } from '../../../../../constants/ComponentTypes'
import {
  fundraisingRoundMenu,
  fundraisingFinancingTypeMenu,
  fundraisingInvestorTypeMenu,
  fundraisingAmountTypeMenu,
} from '../../../../../constants/SidebarValues';
import ArrowToolTip from "../../../../common/ArrowToolTip";
import HelpIcon from "../../../../Icons/HelpIcon";

const styles = () => ({
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%',
    fontSize: '13px',
    borderBottom: '1px solid #D8DBDF',
    boxSizing: 'border-box',
    padding: '0px 10px 18px 10px',
    marginTop: '18px',
  },

  col1: {
    flex: 1,
    color: '#71737B',
  },

  col2: {
    paddingLeft: '15px',
    flex: 3,
    color: '#181819',
  },

  promotePlanLink: {
    color: '#1A65FF',
    fontSize: '11px',
    textDecoration: 'none',
    cursor: 'pointer',
    marginTop: '10px',
  },

  link: {
    textDecoration: 'none',
    cursor: 'pointer',
  },

  tooltip: {
    margin: "auto 0 auto 2px",
  },
});

/**
 * 企業の基本情報を表示するコンポーネント
 */
class CompanyBasicInfo extends React.Component {
  /**
   * プロモーションプランのリンクがクリックされた時のハンドラ
   * @param {string} title - クリックされた項目のタイトル
   * @returns {void}
   */
  promotePlanLinkClickHandler = (title) => {
    if (title === '部門電話番号') {
      this.props.togglePromotePlanChangeDialog(PromotePlanDialogTypes.DEPARTMENT_TEL);
    }
  }

  /**
   * 基本情報の1行を描画する
   * @param {{ title: string, value: string | null, isBlur?: boolean }} item - 表示する項目
   * @param {number} index - 項目のインデックス
   * @returns {JSX.Element | undefined}
   */
  renderRow = (item, index) => {
    const { classes } = this.props;
    if (item && item.value && item.value !== '-') {
      return (
        <div className={classes.row} key={index}>
          {item.title === '部門電話番号' ? (
            <div className={classes.col1} style={{ display: "flex" }}>
              {item.title}
              {this.renderToolTip(`こちらの番号は弊社が独自に調査した番号になりますが、異動・辞任・退職・出向・転勤などにより電話番号が変わっている可能性がございますのであらかじめご了承くださいませ。`)}
            </div>
          ) : (
            <div className={classes.col1}>{item.title}</div>
          )}
          <div className={classes.col2}>
            <div style={{ filter: item.isBlur ? "blur(5px)" : ""}}>{item.isBlur ? "xx-xxxx-xxxx" : item.value}</div>
            {item.isBlur && (
              <div className={classes.promotePlanLink} onClick={() => this.promotePlanLinkClickHandler(item.title)}>詳しくはこちら</div>
            )}
          </div>
        </div>
      );
    }
  }

  /**
   * ツールチップを表示する
   *
   * @param {string} tooltipText - ツールチップに表示するテキスト
   * @returns {JSX.Element}
   */
  renderToolTip = (tooltipText) => {
    const { classes } = this.props
    return (
      <div className={classes.tooltip}>
        <ArrowToolTip
          text={tooltipText}
          tooltipMaxWidth={"300"}
          placement={'bottom'}
        >
          <HelpIcon
            width={12}
            height={12}
            color="#71737B"
          />
        </ArrowToolTip>
      </div>
    )
  }

  /**
   * 会社情報の参照元のリンクを表示する
   * @returns {JSX.Element | null}
   */
  renderCorporationReferenceLink = () => {
    const { item } = this.props
    if (item && item.corporation_source_url) {
      const source_name =
        item.corporation_source_url.indexOf('info.gbiz.go.jp') !== -1 ? 'gBizInfo' :
        item.corporation_source_url.indexOf('edinet-fsa') !== -1 ? 'EDINET' :
        `${item.company_name}の企業情報`
      return (
        this.renderLink(item.corporation_source_url, source_name)
      );
    }
    return null;
  }

  /**
   * 外部リンクを描画する
   * @param {string} source_url - リンク先のURL
   * @param {string} source_name - 表示するリンク名
   * @returns {JSX.Element}
   */
  renderLink = (source_url, source_name) => {
    const { classes } = this.props
    return (
      <span>
        参照：
        <a
          className={classes.link}
          href={source_url}
          target="_blank"
          rel="noopener"
          style={{ color: '#1A65FF' }}
          >
            {source_name}
          </a>
      </span>
    )
  }

  /**
   * 資金調達金額をフォーマット
   * @param {number | null | undefined} amount - 金額（円）
   * @returns {string | null}
   */
  formatFundraisingAmount = (amount) => {
    if (!amount) return null;
    const OKU = 100000000;
    const MAN = 10000;
    if (amount >= OKU) {
      return `${(amount / OKU).toLocaleString()}億円`;
    }
    if (amount >= MAN) {
      return `${(amount / MAN).toLocaleString()}万円`;
    }
    return `${amount.toLocaleString()}円`;
  }

  /**
   * UNIXタイムスタンプを日付フォーマット
   * @param {number | null | undefined} timestamp - UNIXタイムスタンプ（秒）
   * @returns {string | null}
   */
  formatFundraisingDate = (timestamp) => {
    if (!timestamp) return null;
    const MS_PER_SEC = 1000;
    const date = new Date(timestamp * MS_PER_SEC);
    return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
  }

  /**
   * 調達手段を日本語に変換
   * @param {string | null | undefined} type - 調達手段のコード
   * @returns {string | null}
   */
  formatFinancingType = (type) => {
    if (!type) return null;
    const item = fundraisingFinancingTypeMenu.find(m => m.value === type);
    return item ? item.name : type;
  }

  /**
   * ラウンドを日本語に変換
   * @param {string | null | undefined} round - ラウンドのコード
   * @returns {string | null}
   */
  formatRound = (round) => {
    if (!round) return null;
    const item = fundraisingRoundMenu.find(m => m.value === round);
    return item ? item.name : round;
  }

  /**
   * 投資家タイプ配列を日本語に変換
   * @param {string[] | null | undefined} types - 投資家タイプのコード配列
   * @returns {string | null}
   */
  formatInvestorTypes = (types) => {
    if (!types || types.length === 0) return null;
    return types.map(t => {
      const item = fundraisingInvestorTypeMenu.find(m => m.value === t);
      return item ? item.name : t;
    }).join('、');
  }

  /**
   * 金額タイプを日本語に変換
   * @param {string | null | undefined} type - 金額タイプのコード
   * @returns {string | null}
   */
  formatAmountType = (type) => {
    if (!type) return null;
    const item = fundraisingAmountTypeMenu.find(m => m.value === type);
    return item ? item.name : type;
  }

  /**
   * コンポーネントを描画する
   * @returns {JSX.Element}
   */
  render () {
    const { item, user } = this.props;
    const basicInfo = [
      {title: '本社・本店', value: item.address},
      {title: '電話番号', value: item.shikiho_tel},
      {title: '部門電話番号', value: formatDepartmentTel(item), isBlur: !isEnableDisplayDepartmentTel(user)},
      {title: '従業員数', value: item.number_of_employees},
      {title: '従業員数（連結）', value: item.include_consolidated_number_of_employees},
      {title: '店舗数', value: item.number_of_stores ? `${item.number_of_stores.toLocaleString()}店舗` : null},
      {title: "設立日", value: item.detail ? item.detail['設立日'] : ''},
      {title: '資本金', value: item.capital_stock},
      {title: '売上高', value: item.amount_of_sales},
      {title: '上場市場', value: item.listing_type},
      {title: '決算月', value: item.detail ? item.detail['決算月'] : ''},
      {title: '平均年齢', value: item.detail ? item.detail['平均年齢'] : ''},
      {title: '平均年収', value: item.detail ? item.detail['年間給与'] : ''},
      {title: '法人番号', value: item.corporate_number},
      {title: '証券コード', value: item.shikiho_stock_code},
      {title: '直近の調達日', value: this.formatFundraisingDate(item.latest_fundraising_announced_at)},
      {title: '調達金額', value: this.formatFundraisingAmount(item.latest_fundraising_amount)},
      {title: '金額タイプ', value: this.formatAmountType(item.latest_fundraising_amount_type)},
      {title: 'ラウンド', value: this.formatRound(item.latest_fundraising_series)},
      {title: '調達手段', value: this.formatFinancingType(item.latest_fundraising_financing_type)},
      {title: '投資家タイプ', value: this.formatInvestorTypes(item.latest_fundraising_investor_types)},
    ];

    return (
      <React.Fragment>
        <Title title='基本情報' style={{margin: '0'}}/>
        {basicInfo.map((info, i) => this.renderRow(info, i))}
        <div style={{ textAlign: 'end', marginTop: '20px', fontSize: '11px' }}>
          {this.renderCorporationReferenceLink()}
        </div>
      </React.Fragment>
    );
  }
}

CompanyBasicInfo.propTypes = {
  classes: PropTypes.object.isRequired,
  item: PropTypes.object,
  user: PropTypes.object,
  togglePromotePlanChangeDialog: PropTypes.func,
}

PropTypes.defaultValue = {
}

export default withStyles(styles)(CompanyBasicInfo);



// WEBPACK FOOTER //
// ./src/components/ExecutiveList/dialogs/ExecDetailItemDialog/parts/CompanyBasicInfo.jsx